import { Divider, Drawer, Menu, MenuProps } from 'antd';
import Sider from 'antd/es/layout/Sider';
import { items } from './items';
import { useLocation, useNavigate } from 'react-router-dom';
import { SidebarHeader } from './components/sidebar-header';
import { sidebar_width } from '@shared/constants';
import { SidebarNumbersView } from '@modules/channels/components/sidebar-numbers/sidebar-numbers-view';
import { SidebarTeamsView } from '@modules/teams/components/sidebar-teams/sidebar-teams-view';
import { SidebarViewProps } from '@modules/channels/channel.types';
import { SidebarMembersView } from '@modules/members/components/sidebar-members/sidebar-members-view';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import { ROLE_MAPS } from '@shared/constants/roles';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { hasPlanAccess } from '@shared/utils/roles/has-plan-access.helper';
import { planPrivilegeStore } from '@shared/store/plan-privilege.store';
import { rolePrivilegeStore } from '@shared/store/role-privilege.store';

export function SidebarView(props: SidebarViewProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { layoutContext } = props;
  const { sidebarCollapsed, closeSidebarMobileDrawer, sidebarMobileDrawerOpen } = layoutContext;
  const { isMobile, isSmallerDevice } = useScreenSize();
  const { t } = useTranslation();
  const { planPrivileges } = planPrivilegeStore();
  const { rolePrivileges } = rolePrivilegeStore();

  const privilegeItems = useMemo(
    () =>
      items(t)
        .filter(({ rolePrivilege }) => {
          if (!rolePrivilege) return true;
          const [hasAccess] = hasRoleAccess(rolePrivilege);
          return hasAccess;
        })
        .filter(({ planPrivilege }) => {
          if (!planPrivilege) return true;
          const [hasAccess] = hasPlanAccess(planPrivilege);
          return hasAccess;
        })
        .map(({ rolePrivilege: _rolePrivilege, planPrivilege: _planPrivilege, mobileKey, ...item }) => ({
          ...item,
          key: isMobile && mobileKey ? mobileKey : (item.key as string),
        })),
    [isMobile, t, planPrivileges, rolePrivileges]
  );

  const [canViewMembers] = hasRoleAccess([ROLE_MAPS.SETTINGS_MEMBERS_LIST]);

  const activeKey = useMemo(() => {
    return selectedKeys(pathname, privilegeItems);
  }, [pathname]);

  const SidebarComponent = (sidebarCollapsed: boolean) => {
    const updatedProps = {
      ...props,
      layoutContext: {
        ...props.layoutContext,
        sidebarCollapsed,
      },
    };
    return (
      <Sider
        theme="light"
        width={sidebar_width}
        trigger={null}
        collapsible
        collapsed={sidebarCollapsed}
        className="border-r border-gray-500 overflow-y-auto overflow-x-hidden sticky h-dvh"
      >
        <SidebarHeader collapsed={sidebarCollapsed} />
        <span className="sidebar">
          <Menu
            selectedKeys={activeKey ? [activeKey] : []}
            onClick={(e) => {
              const currentItem = privilegeItems.find((item) => item.key === e.key);
              if (currentItem?.external) return window.open(currentItem?.external, '_blank');
              closeSidebarMobileDrawer();
              navigate(e.key);
            }}
            mode="inline"
            inlineCollapsed={sidebarCollapsed}
            items={privilegeItems}
            rootClassName="!pt-3 pb-2!"
            className={clsx(sidebarCollapsed && '[&_.ant-menu-item]:pl-6!')}
          />
        </span>
        <>
          <Divider rootClassName={clsx('!mt-2 mb-1! border-gray-500!', !sidebarCollapsed && 'ml-4!')} />
          <SidebarNumbersView {...updatedProps} />
        </>
        {canViewMembers && (
          <>
            <Divider rootClassName={clsx('!mt-2 mb-1! border-gray-500!', !sidebarCollapsed && 'ml-4!')} />
            <SidebarMembersView {...updatedProps} />
          </>
        )}
        <Divider rootClassName={clsx('!mt-2 mb-1! border-gray-500!', !sidebarCollapsed && 'ml-4!')} />
        <SidebarTeamsView {...updatedProps} />
        {/* Adds spacing on mobile and desktop */}
        <div className="h-4" />
      </Sider>
    );
  };

  return (
    <>
      {isSmallerDevice && (
        <Drawer
          closable
          onClose={closeSidebarMobileDrawer}
          placement="left"
          width={sidebar_width}
          open={sidebarMobileDrawerOpen}
          styles={{
            body: {
              padding: 0,
              overflow: 'hidden',
            },
          }}
          className="xl:hidden sidebar-drawer"
        >
          {SidebarComponent(false)}
        </Drawer>
      )}
      <div className="hidden md:block">{SidebarComponent(sidebarCollapsed)}</div>
    </>
  );
}

// always active parent despite sub navigations
function selectedKeys(pathname: string, items: MenuProps['items']) {
  const matchItem = items?.find((item) => {
    const path = String(item?.key)?.split('/')?.[1]; // this give path like dashboard, settings, analytics without slash because of split
    return pathname.startsWith('/' + path);
  });
  return matchItem?.key?.toString();
}
